import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { alertSupportsWorkerUpload, getAlertUploadRequirementId } from './workerAlertUtils';

export default function WorkerComplianceDueCard({
  alerts,
  formatDate,
  onUpload,
  uploading,
}) {
  if (!alerts?.length) return null;

  return (
    <Card className="rounded-2xl border-amber-200/80 bg-gradient-to-br from-amber-50/50 to-white shadow-[0_8px_30px_rgba(217,119,6,0.08)]" data-testid="compliance-due-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg text-amber-900 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Renewals to keep you work-ready
        </CardTitle>
        <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
          These are the items that could affect shifts. Upload where shown; manager-led checks will be handled by the office.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2.5">
          {alerts.slice(0, 6).map((alert, idx) => {
            const uploadId = getAlertUploadRequirementId(alert);
            const canUpload = alertSupportsWorkerUpload(alert) && uploadId;
            return (
              <div
                key={`compliance-due-${idx}`}
                className="flex flex-col gap-2 rounded-xl border border-amber-100/80 bg-white/90 p-3.5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{alert.title}</p>
                  <p className="text-xs text-slate-600">
                    {alert.days_left <= 0 ? 'Overdue' : `Due in ${alert.days_left} days`}
                    {alert.date ? ` · ${formatDate(alert.date)}` : ''}
                  </p>
                  {canUpload ? (
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                      <RefreshCw className="h-3.5 w-3.5" />
                      Upload the new evidence here and your manager will review it.
                    </p>
                  ) : null}
                  {!canUpload && alert.action_note ? (
                    <p className="text-xs text-slate-500 mt-1">{alert.action_note}</p>
                  ) : null}
                </div>
                {canUpload ? (
                  <Button
                    size="sm"
                    variant={alert.urgent ? 'default' : 'outline'}
                    className={`w-full sm:w-auto gap-1 ${alert.urgent ? 'bg-amber-700 hover:bg-amber-800' : ''}`}
                    onClick={() => onUpload(uploadId)}
                    disabled={uploading === uploadId}
                    data-testid={`compliance-due-upload-${alert.type}`}
                  >
                    {uploading === uploadId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Upload renewal
                  </Button>
                ) : (
                  <Badge className="bg-slate-100 text-slate-700 shrink-0 gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Manager-led
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
